import { ProFormaModelIds } from 'constants/proForma';
import { IModelExportConfig } from './types';

// ============================================
// Base Configuration (shared fields across all models)
// ============================================

const BASE_CONFIG = {
    buildingInfoTab: {
        propertyInfo: {
            dealName: 'Deal name',
            address: 'Address',
            city: 'City',
            state: 'State',
            zip: 'Zip',
            county: 'County',
        },
        buildingSpecs: {
            numberOfBuildings: 'Number of buildings',
            averageStories: 'Average stories',
            grossSF: 'Gross SF',
            rentableSF: 'Rentable SF',
            efficiencyRatio: 'Efficiency ratio',
        },
        parking: {
            surfaceSpaces: 'Surface spaces',
            structuredSpaces: 'Structured spaces',
            undergroundSpaces: 'Underground spaces',
            parkingRatio: 'Parking ratio',
        },
        keyDates: {
            analysisStart: 'Analysis start',
            constructionStart: 'Construction start',
            constructionDuration: 'Construction duration',
            operationsStart: 'Operations start',
            holdPeriod: 'Hold period',
            projectedSaleDate: 'Projected sale date',
        },
        operatingAssumptions: {
            operationStartDate: 'Operation start date',
            occupancyUponStabilization: 'Occupancy upon stabilization',
            rentGrowthAnnual: 'Rent growth (annual)',
            lengthOfLease: 'Length of lease',
        },
    },
    sourcesUsesTab: {
        uses: {
            sectionHeader: 'DEVELOPMENT BUDGET (USES)',
            amountColumn: 'Amount',
            costPerGsfColumn: 'Cost / GSF',
            totalLabel: 'TOTAL USES',
        },
        sources: {
            sectionHeader: 'SOURCES OF CAPITAL',
            amountColumn: 'Amount',
            percentOfTotalColumn: '% of Total',
            rateColumn: 'Rate',
            totalLabel: 'TOTAL SOURCES',
        },
    },
    cashFlowTab: {
        developmentBudget: {
            sectionHeader: 'DEVELOPMENT BUDGET',
            totalDevelopmentCosts: 'Total Development Costs',
        },
        constructionFinancing: {
            sectionHeader: 'CONSTRUCTION FINANCING',
            loanDraws: 'Construction Loan Draws',
            interest: 'Construction Loan Interest',
            financingFees: 'Financing Fees',
            constructionLoanPayoff: 'Construction Loan Payoff',
        },
        adjustments: {
            sectionHeader: 'INCOME ADJUSTMENTS',
            totalIncomeAdjustments: 'Total Income Adjustments',
        },
        effectiveGrossRevenue: {
            sectionHeader: 'EFFECTIVE GROSS REVENUE',
        },
        expenses: {
            sectionHeader: 'OPERATING EXPENSES',
            totalOperatingExpenses: 'Total Operating Expenses',
        },
        netOperatingIncome: {
            sectionHeader: 'NET OPERATING INCOME',
        },
        capex: {
            sectionHeader: 'CAPITAL EXPENDITURES',
            totalCapitalExpenditures: 'Total Capital Expenditures',
        },
        cashFlowFromOperations: {
            sectionHeader: 'CASH FLOW FROM OPERATIONS',
        },
        permanentDebt: {
            sectionHeader: 'PERMANENT DEBT',
            funding: 'Permanent Loan Funding',
            closingCosts: 'Permanent Loan Closing Costs',
            debtService: 'Permanent Loan Debt Service',
            payoff: 'Permanent Loan Payoff',
        },
        saleProceeds: {
            sectionHeader: 'SALE PROCEEDS',
            grossSaleProceeds: 'Gross Sale Proceeds',
            sellingCosts: 'Selling Costs',
            netSaleProceeds: 'Net Sale Proceeds',
        },
        netLeveredCashFlow: {
            label: 'NET LEVERED CASH FLOW',
        },
        occupancyMetrics: {
            sectionHeader: 'OCCUPANCY METRICS',
            leasedPercent: 'Leased %',
            economicOccupancy: 'Economic Occupancy %',
        },
    },
    dispositionTab: {
        assumptions: {
            saleDate: 'Sale Date:',
            sellingCosts: 'Selling Costs:',
            saleMonth: 'Sale Month:',
            marketCapRate: 'Market Cap Rate:',
        },
        stabilizedValuation: {
            sectionHeader: 'STABILIZED VALUATION',
            stabilizedValuation: 'Stabilized valuation',
            perRentableSf: 'Per rentable SF',
        },
        saleProceeds: {
            sectionHeader: 'SALE PROCEEDS',
            grossSaleProceeds: 'Gross Sale Proceeds',
            sellingCosts: 'Selling Costs',
            netSaleProceeds: 'Net Sale Proceeds',
            loanPayoff: 'Loan Payoff',
            equityProceedsFromSale: 'Equity Proceeds from Sale',
        },
    },
    returnsTab: {
        propertyReturns: {
            sectionHeader: 'PROPERTY-LEVEL RETURNS',
            irr: 'Internal Rate of Return (IRR)',
            equityMultiple: 'Equity Multiple (EMX)',
            roi: 'Return on Investment (ROI)',
        },
        developmentReturns: {
            sectionHeader: 'DEVELOPMENT RETURNS',
            yieldOnCost: 'Yield on cost',
            marketCapRate: 'Market cap rate',
            developmentSpread: 'Development spread',
        },
        keyStatistics: {
            sectionHeader: 'KEY STATISTICS',
            operatingExpenseRatio: 'Operating expense ratio',
            cashOnCashReturn: 'Cash-on-cash return',
            stabilizedNoi: 'Stabilized NOI',
        },
    },
};

// ============================================
// Apartment Configuration
// ============================================

const APARTMENT_CONFIG: IModelExportConfig = {
    buildingInfoTab: {
        ...BASE_CONFIG.buildingInfoTab,
        buildingSpecs: {
            ...BASE_CONFIG.buildingInfoTab.buildingSpecs,
            totalResidentialUnits: 'Total residential units',
        },
        parking: {
            ...BASE_CONFIG.buildingInfoTab.parking,
            ratioLabel: 'per unit',
        },
        operatingAssumptions: {
            ...BASE_CONFIG.buildingInfoTab.operatingAssumptions,
            percentPreLeased: '% pre-leased',
            leaseUpPaceUnitsPerMonth: 'Lease-up pace (units/month)',
            initialFreeRent: 'Initial free rent',
            stabilizedFreeRent: 'Stabilized free rent',
        },
    },
    sourcesUsesTab: {
        ...BASE_CONFIG.sourcesUsesTab,
        uses: {
            ...BASE_CONFIG.sourcesUsesTab.uses,
            costPerUnitColumn: 'Cost / Unit',
        },
    },
    cashFlowTab: {
        ...BASE_CONFIG.cashFlowTab,
        income: {
            type: 'apartment',
            sectionHeader: 'INCOME',
            grossRentLabel: 'Gross Rent - Apartment Units',
            concessions: 'Concessions',
            gainLossToLease: 'Gain/Loss to Lease',
            nonRevenueUnits: 'Non-Revenue Units',
            totalRentalIncome: 'Total Rental Income',
            totalOtherIncome: 'Total Other Income',
            totalPotentialIncome: 'Total Potential Income',
        },
    },
    dispositionTab: {
        ...BASE_CONFIG.dispositionTab,
        stabilizedValuation: {
            ...BASE_CONFIG.dispositionTab.stabilizedValuation,
            perUnit: 'Per unit',
        },
    },
    returnsTab: BASE_CONFIG.returnsTab,
};

// ============================================
// Commercial Configuration (base for Industrial, Office, Retail, and Data Center)
// Income section = type: 'commercial' (Tenants and Floor Estimates) vs Apartment (Unit Mix based Gross Rent)
// ============================================

const COMMERCIAL_CONFIG: IModelExportConfig = {
    buildingInfoTab: {
        ...BASE_CONFIG.buildingInfoTab,
        parking: {
            ...BASE_CONFIG.buildingInfoTab.parking,
            ratioLabel: 'per 1,000 RSF',
        },
        operatingAssumptions: {
            ...BASE_CONFIG.buildingInfoTab.operatingAssumptions,
            initialFreeRent: 'Initial free rent',
            stabilizedFreeRent: 'Stabilized free rent',
        },
    },
    sourcesUsesTab: {
        ...BASE_CONFIG.sourcesUsesTab,
        uses: {
            ...BASE_CONFIG.sourcesUsesTab.uses,
            costPerRsfColumn: 'Cost / RSF',
        },
    },
    cashFlowTab: {
        ...BASE_CONFIG.cashFlowTab,
        income: {
            type: 'commercial',
            sectionHeader: 'INCOME',
            expenseReimbursements: 'Expense Reimbursements',
            totalRentalIncome: 'Total Rental Income',
            totalOtherIncome: 'Total Other Income',
            totalPotentialIncome: 'Total Potential Income',
        },
    },
    dispositionTab: {
        ...BASE_CONFIG.dispositionTab,
        stabilizedValuation: {
            ...BASE_CONFIG.dispositionTab.stabilizedValuation,
            perGrossSf: 'Per gross SF',
        },
    },
    returnsTab: BASE_CONFIG.returnsTab,
};

const INDUSTRIAL_CONFIG: IModelExportConfig = {
    ...COMMERCIAL_CONFIG,
    buildingInfoTab: {
        ...COMMERCIAL_CONFIG.buildingInfoTab,
        parking: {
            ...COMMERCIAL_CONFIG.buildingInfoTab.parking,
            trailerSpaces: 'Trailer spaces',
            ratioLabel: 'per 1,000 GSF',
        },
    },
};

const DATA_CENTER_CONFIG: IModelExportConfig = {
    buildingInfoTab: {
        ...COMMERCIAL_CONFIG.buildingInfoTab,
        parking: {
            ...COMMERCIAL_CONFIG.buildingInfoTab.parking,
            ratioLabel: undefined,
        },
        operatingAssumptions: {
            ...COMMERCIAL_CONFIG.buildingInfoTab.operatingAssumptions,
            initialFreeRent: undefined,
            stabilizedFreeRent: undefined,
        },
    },
    sourcesUsesTab: COMMERCIAL_CONFIG.sourcesUsesTab,
    cashFlowTab: {
        ...COMMERCIAL_CONFIG.cashFlowTab,
        income: {
            ...COMMERCIAL_CONFIG.cashFlowTab.income,
            type: 'data_center' as const,
        },
    },
    dispositionTab: COMMERCIAL_CONFIG.dispositionTab,
    returnsTab: COMMERCIAL_CONFIG.returnsTab,
};

// ============================================
// Model Config Registry
// ============================================

const MODEL_EXPORT_CONFIGS: Record<number, IModelExportConfig> = {
    [ProFormaModelIds.APARTMENT]: APARTMENT_CONFIG,
    [ProFormaModelIds.INDUSTRIAL]: INDUSTRIAL_CONFIG,
    [ProFormaModelIds.OFFICE]: COMMERCIAL_CONFIG,
    [ProFormaModelIds.RETAIL]: COMMERCIAL_CONFIG,
    [ProFormaModelIds.DATA_CENTER]: DATA_CENTER_CONFIG,
};

export const getModelExportConfig = (modelId: number): IModelExportConfig => {
    const config = MODEL_EXPORT_CONFIGS[modelId];
    if (!config) {
        throw new Error(`No Excel export config for model ID ${modelId}`);
    }
    return config;
};
